import { Link } from "@inertiajs/react"
import { SeoHead } from "@/components/SeoHead"
import { relFor } from "@/lib/affiliate"
import { MarketingLayout } from "@/components/marketing/MarketingLayout"
import { Section, Eyebrow } from "@/components/marketing/sections/Section"

const DESCRIPTION =
  "The tools FullCast uses to produce client podcasts — hosting, recording, editing, scheduling, distribution. The actual stack, not a hypothetical one."

type Tool = {
  name: string
  blurb: string
  url: string         // local affiliate redirect or external vendor URL
  affiliate?: boolean
}

type Section = {
  title: string
  intro: string
  tools: Tool[]
}

const SECTIONS: Section[] = [
  {
    title: "Hosting & distribution",
    intro:
      "The media host is the engine — it stores your audio, generates the RSS feed, and pushes to every directory. Choose deliberately, because moving later is annoying.",
    tools: [
      {
        name: "Captivate",
        blurb:
          "Our default. We host every FullCast client show here. Built for serious podcasters; strong analytics; dynamic content insertion that lets us update CTAs across the entire back catalog without re-uploading anything; a team that actually picks up the phone when something gets weird.",
        url: "/captivate",
        affiliate: true,
      },
    ],
  },
  {
    title: "Recording",
    intro:
      "Remote interviews need local-first recording — each side records their own audio, then uploads. Cloud-recorded interviews are vulnerable to network glitches; local recording isn't.",
    tools: [
      {
        name: "Riverside.fm",
        blurb:
          "Browser-based, records each guest locally in high-resolution audio and video, then syncs separately. The current default for client interviews.",
        url: "https://riverside.fm",
      },
      {
        name: "SquadCast",
        blurb:
          "The longer-standing player in local-first remote recording. Now owned by Descript; still strong for audio-only interviews.",
        url: "https://fullcast.co/squadcast",
      },
    ],
  },
  {
    title: "Editing & production",
    intro:
      "What turns raw conversations into finished episodes. The difference between a podcast and a recording.",
    tools: [
      {
        name: "Descript",
        blurb:
          "Edit audio by editing the transcript. Removes filler words, fixes mistakes, generates clips. Fast for high-volume podcast production.",
        url: "https://fullcast.co/descript",
      },
      {
        name: "Auphonic",
        blurb:
          "Audio leveling, denoising, loudness normalization. Pure mastering automation — upload, get a clean file back. Indispensable for batch-processing.",
        url: "https://auphonic.com",
      },
    ],
  },
  {
    title: "Scheduling",
    intro:
      "Booking guests is a friction point you fix once. Don't lose interviews to email tag.",
    tools: [
      {
        name: "Cal.com",
        blurb:
          "Open-source scheduling — the recording booking page for every client we run. Cleaner UX than the alternatives and integrates with the way we already work.",
        url: "https://cal.com",
      },
    ],
  },
  {
    title: "Email & audience",
    intro:
      "The newsletter is the asset. Every episode of every show should be feeding it.",
    tools: [
      {
        name: "Kit (formerly ConvertKit)",
        blurb:
          "What we use for the FullCast list and for client newsletter setups when they want one. Strong creator-focused tooling, fair pricing, and the segmentation we need to run real campaigns.",
        url: "https://fullcast.co/kit",
      },
    ],
  },
  {
    title: "Equipment",
    intro:
      "Gear matters less than people think. A $60 mic and a great guest beats a $2,000 studio and a boring interview. But there's a floor below which audio quality actively costs you listeners.",
    tools: [
      {
        name: "Shure MV7+",
        blurb:
          "The smart-money pick. Hybrid USB/XLR, onboard DSP, real-time denoising. Gets you 85–90% of the way to its famous big brother (the SM7B) without needing a cloud lifter or fancy interface. Solo hosts and remote interviewers — start here.",
        url: "https://amzn.to/4oW9vjT",
      },
      {
        name: "Shure SM7B",
        blurb:
          "The broadcast classic. Pairs with an XLR interface and (usually) a Cloudlifter for clean gain. The right answer if you're committed to a long-running show and want the gold standard.",
        url: "https://amzn.to/4fbGfST",
      },
      {
        name: "Focusrite Scarlett Solo (4th gen)",
        blurb:
          "One XLR input, clean preamps, rock-solid drivers. All a solo XLR host needs and a small fraction of what fancier interfaces cost.",
        url: "https://amzn.to/4eQ4XXv",
      },
    ],
  },
]

export default function Stack() {
  return (
    <MarketingLayout>
      <SeoHead title="The FullCast Stack" description={DESCRIPTION} />

      <Section containerClassName="max-w-3xl">
        <Eyebrow>FullCast · Tools we actually use</Eyebrow>
        <h1 className="display-1 mt-4">The FullCast Stack</h1>
        <p className="mt-5 text-lg text-ink-body">
          Every tool below is one we use to produce client podcasts, run the
          FullCast newsletter, or record our own conversations. Not "tools we
          recommend in theory." The real stack.
        </p>
        <p className="mt-3 text-sm text-ink-muted">
          Some links are affiliate links — if you sign up, we may earn a small
          commission at no cost to you. See our{" "}
          <Link href="/disclosure" className="text-accent-display">
            affiliate disclosure
          </Link>{" "}
          for what that means.
        </p>
      </Section>

      {SECTIONS.map((section) => (
        <Section
          key={section.title}
          containerClassName="max-w-3xl"
          className="border-t border-hairline"
        >
          <h2 className="display-2">{section.title}</h2>
          <p className="mt-3 text-ink-body">{section.intro}</p>
          <div className="mt-8 space-y-6">
            {section.tools.map((tool) => (
              <div
                key={tool.name}
                className="rounded-xl border border-hairline bg-surface p-6"
              >
                <div className="flex items-start justify-between gap-4">
                  <h3 className="m-0">{tool.name}</h3>
                  {tool.affiliate && (
                    <span className="rounded-full border border-accent-display px-2 py-0.5 text-xs uppercase tracking-wider text-accent-display">
                      affiliate
                    </span>
                  )}
                </div>
                <p className="mt-3 text-ink-body">{tool.blurb}</p>
                <a
                  href={tool.url}
                  target={tool.url.startsWith("/") ? undefined : "_blank"}
                  rel={relFor(tool.url)}
                  className="mt-4 inline-block text-sm font-medium text-accent-display no-underline hover:underline"
                >
                  Visit {tool.name} →
                </a>
              </div>
            ))}
          </div>
        </Section>
      ))}

      <Section containerClassName="max-w-2xl" className="border-t border-hairline">
        <Eyebrow>Want this run for you?</Eyebrow>
        <h2 className="display-2 mt-4">FullCast runs the whole stack for clients.</h2>
        <p className="mt-3 text-ink-body">
          Done-for-you podcast strategy and production for founders, coaches,
          and experts who want a show that actually compounds — without
          managing four vendors who don't talk to each other.
        </p>
        <div className="mt-6">
          <Link
            href="/apply"
            className="inline-block rounded-xl bg-ink-display px-6 py-3 text-page no-underline hover:bg-ink-body"
          >
            Apply for a Fit Call →
          </Link>
        </div>
      </Section>
    </MarketingLayout>
  )
}
